import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Database,
  Download,
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  HardDrive,
  Server,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { AdminHeader } from '../../../shared/components'
import RestoreWizardModal from '../components/RestoreWizardModal'
import {
  adminBackupApi,
  type BackupFile,
  type BackupStatus,
} from '../../../shared/services/adminBackupApi'

const BackupManagementPage: React.FC = () => {
  const [backups, setBackups] = useState<BackupFile[]>([])
  const [backupStatus, setBackupStatus] = useState<BackupStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  // 복원 마법사 상태
  const [showRestoreWizard, setShowRestoreWizard] = useState(false)
  const [selectedBackupForRestore, setSelectedBackupForRestore] =
    useState<BackupFile | null>(null)

  // 다중 선택 상태
  const [selectedBackups, setSelectedBackups] = useState<Set<string>>(new Set())
  const [selectAll, setSelectAll] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    await Promise.all([fetchBackups(), fetchBackupStatus()])
    setLastUpdated(new Date())
  }

  const fetchBackups = async () => {
    try {
      setError('')
      setBackups(await adminBackupApi.list())
    } catch (error: any) {
      console.error('백업 목록 조회 실패:', error)
      setError('백업 목록 조회 실패')
      toast.error('백업 목록을 불러올 수 없습니다')
      setBackups([])
    } finally {
      setLoading(false)
    }
  }

  const fetchBackupStatus = async () => {
    try {
      setBackupStatus(await adminBackupApi.status())
    } catch (error) {
      console.warn('백업 상태 조회 실패:', error)
    }
  }

  const handleCreateBackup = async () => {
    try {
      setCreating(true)
      await adminBackupApi.create('manual')
      toast.success('백업이 성공적으로 생성되었습니다')
      await fetchBackups()
    } catch (error: any) {
      console.error('백업 생성 실패:', error)
      toast.error('백업 생성에 실패했습니다')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteBackup = async (filename: string) => {
    if (!confirm('이 백업 파일을 삭제하시겠습니까?')) {
      return
    }

    try {
      await adminBackupApi.remove(filename)
      toast.success('백업 파일이 삭제되었습니다')
      await fetchBackups()
    } catch (error: any) {
      console.error('백업 파일 삭제 실패:', error)
      toast.error('백업 파일 삭제에 실패했습니다')
    }
  }

  // 다중 선택 관련 함수들
  const handleSelectBackup = (filename: string) => {
    const newSelected = new Set(selectedBackups)
    if (newSelected.has(filename)) {
      newSelected.delete(filename)
    } else {
      newSelected.add(filename)
    }
    setSelectedBackups(newSelected)
    setSelectAll(newSelected.size === backups.length)
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedBackups(new Set())
    } else {
      setSelectedBackups(new Set(backups.map(b => b.filename)))
    }
    setSelectAll(!selectAll)
  }

  const handleDeleteSelected = async () => {
    if (selectedBackups.size === 0) {
      toast.error('삭제할 백업 파일을 선택해주세요')
      return
    }

    if (
      !confirm(
        `선택한 ${selectedBackups.size}개의 백업 파일을 삭제하시겠습니까?`
      )
    ) {
      return
    }

    const deletePromises = Array.from(selectedBackups).map(filename =>
      adminBackupApi.remove(filename)
    )

    try {
      // 일부만 실패해도 나머지 결과를 집계해야 하므로 allSettled 를 쓴다
      const results = await Promise.allSettled(deletePromises)
      const successCount = results.filter(r => r.status === 'fulfilled').length
      const failCount = results.length - successCount

      if (successCount > 0) {
        toast.success(`${successCount}개의 백업 파일이 삭제되었습니다`)
      }
      if (failCount > 0) {
        toast.error(`${failCount}개의 백업 파일 삭제에 실패했습니다`)
      }

      setSelectedBackups(new Set())
      setSelectAll(false)
      await fetchBackups()
    } catch (error: any) {
      console.error('백업 파일 삭제 실패:', error)
      toast.error('백업 파일 삭제에 실패했습니다')
    }
  }

  const handleRestoreBackup = async (backup: BackupFile) => {
    setSelectedBackupForRestore(backup)
    setShowRestoreWizard(true)
  }

  const handleRestoreComplete = async () => {
    await fetchBackups()
    setShowRestoreWizard(false)
    setSelectedBackupForRestore(null)
  }

  const handleRefresh = async () => {
    try {
      setRefreshing(true)
      await loadData()
      toast.success('데이터가 새로고침되었습니다')
    } catch {
      toast.error('새로고침에 실패했습니다')
    } finally {
      setRefreshing(false)
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'hourly':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300'
      case 'manual':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
      case 'daily':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
      case 'weekly':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
      case 'monthly':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'failed':
        return <AlertTriangle className="h-5 w-5 text-red-600" />
      case 'in_progress':
        return <Clock className="h-5 w-5 text-yellow-600" />
      default:
        return <Clock className="h-5 w-5 text-gray-600" />
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            백업 데이터 로딩 중...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader
        title="백업 관리"
        subtitle="데이터베이스 백업을 생성하고 관리합니다"
        showRefresh={true}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
        lastUpdated={lastUpdated}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20"
          >
            <div className="flex">
              <AlertTriangle className="mr-2 h-5 w-5 text-red-400" />
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          </motion.div>
        )}

        {/* Action Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center"
        >
          <div className="flex items-center space-x-4">
            <button
              onClick={handleCreateBackup}
              disabled={creating}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {creating ? (
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              ) : (
                <Database className="mr-2 h-4 w-4" />
              )}
              수동 백업 생성
            </button>

            {selectedBackups.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                선택 항목 삭제 ({selectedBackups.size})
              </button>
            )}
          </div>
        </motion.div>

        {/* Backup Status Card */}
        {backupStatus && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800"
          >
            <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
              백업 상태
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="flex items-center">
                <Server className="mr-3 h-5 w-5 text-indigo-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    자동 백업
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {backupStatus.isScheduled ? '활성화됨' : '비활성화됨'}
                  </p>
                </div>
              </div>
              {backupStatus.lastBackup && (
                <div className="flex items-center">
                  <Clock className="mr-3 h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      마지막 백업
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(
                        backupStatus.lastBackup.createdAt
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center">
                <HardDrive className="mr-3 h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    다음 자동 백업
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {backupStatus.nextScheduled.daily}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Backup Files Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-lg bg-white shadow dark:bg-gray-800"
        >
          <div className="px-4 py-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                백업 파일 목록
              </h3>
              <button
                onClick={fetchBackups}
                className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
            </div>

            {backups.length === 0 ? (
              <div className="py-12 text-center">
                <Database className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                  백업 파일 없음
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  백업 파일이 없습니다. 첫 번째 백업을 생성해보세요.
                </p>
              </div>
            ) : (
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        파일명
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        크기
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        유형
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        상태
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        생성일
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        작업
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                    {backups.map((backup, index) => (
                      <motion.tr
                        key={backup.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="whitespace-nowrap px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedBackups.has(backup.filename)}
                            onChange={() => handleSelectBackup(backup.filename)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {backup.filename}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {backup.sizeFormatted}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getTypeColor(backup.type)}`}
                          >
                            {backup.type}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center">
                            {getStatusIcon(backup.status)}
                            <span className="ml-2 text-sm capitalize text-gray-900 dark:text-white">
                              {backup.status}
                            </span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(backup.createdAt).toLocaleString()}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            {/* 복원 버튼 - 개선된 버전 */}
                            <button
                              onClick={() => handleRestoreBackup(backup)}
                              className="flex items-center rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
                              title="안전한 복원"
                            >
                              <RefreshCw className="mr-1 h-4 w-4" />
                              복원
                            </button>

                            {/* 다운로드 버튼 */}
                            <button
                              onClick={() =>
                                window.open(
                                  `/admin/backup/download/${encodeURIComponent(backup.filename)}`,
                                  '_blank'
                                )
                              }
                              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                              title="다운로드"
                            >
                              <Download className="h-4 w-4" />
                            </button>

                            {/* 삭제 버튼 */}
                            <button
                              onClick={() =>
                                handleDeleteBackup(backup.filename)
                              }
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="삭제"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* 복원 마법사 모달 */}
      <RestoreWizardModal
        isOpen={showRestoreWizard}
        onClose={() => {
          setShowRestoreWizard(false)
          setSelectedBackupForRestore(null)
        }}
        selectedBackup={selectedBackupForRestore}
        onRestoreComplete={handleRestoreComplete}
      />
    </div>
  )
}

export default BackupManagementPage
